import { type AdminOrg, orgCreateSchema } from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  type Column,
  DataTable,
  Dialog,
  DialogCancel,
  Input,
  PageHeader,
  Select,
  StatusBadge,
  useToast,
} from '@fruuit/ui'
import { Building, Pause, Play, Plus, Users } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { errorMessage, isApiError } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { adminApi, adminKeys, useAdminOrgs, useCursorPager } from '../../features/admin-users/api'
import { ReasonDialog } from '../../features/admin-users/ReasonDialog'
import { formatDate } from '../../features/auth/format'
import { MfaRequiredNotice, useStepUp } from '../../features/auth/StepUp'
import { useTitle } from '../../lib/useTitle'

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

/** Create or edit an organisation's name and slug. */
function OrgDialog({
  org,
  open,
  onClose,
}: {
  org: AdminOrg | null
  open: boolean
  onClose: () => void
}) {
  const { toast } = useToast()
  const { guard, dialog } = useStepUp()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [errors, setErrors] = useState<{ name?: string; slug?: string }>({})
  useEffect(() => {
    if (open) {
      setName(org?.name ?? '')
      setSlug(org?.slug ?? '')
      setSlugTouched(org !== null)
      setErrors({})
    }
  }, [open, org])

  const save = useApiMutation(
    (vars: { name: string; slug: string }, ctx) =>
      guard(() =>
        org
          ? adminApi.updateOrg({ id: org.id, revision: org.revision, ...vars }, ctx)
          : adminApi.createOrg(vars, ctx),
      ),
    {
      invalidate: [adminKeys.orgs],
      onSuccess: () => {
        toast({ tone: 'success', message: org ? 'Organisation updated.' : 'Organisation created.' })
        onClose()
      },
    },
  )

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const parsed = orgCreateSchema.safeParse({ name, slug })
    if (!parsed.success) {
      const next: { name?: string; slug?: string } = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as 'name' | 'slug'
        next[key] ??= issue.message
      }
      setErrors(next)
      return
    }
    setErrors({})
    save.mutate(parsed.data)
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={org ? `Edit ${org.name}` : 'New investor organisation'}
        description={
          org
            ? undefined
            : 'Then invite its people from Users. Investors only see what is released and subscribed.'
        }
        footer={
          <>
            <DialogCancel />
            <Button type="submit" form="org-form" loading={save.isPending}>
              {org ? 'Save' : 'Create organisation'}
            </Button>
          </>
        }
      >
        <form id="org-form" onSubmit={onSubmit} className="grid gap-4" noValidate>
          <Input
            label="Name"
            name="name"
            required
            value={name}
            onChange={(event) => {
              setName(event.target.value)
              if (!slugTouched) setSlug(slugify(event.target.value))
            }}
            error={errors.name}
          />
          <Input
            label="Slug"
            name="slug"
            hint="Lower-case letters, digits and hyphens. Used in exports and links."
            required
            value={slug}
            onChange={(event) => {
              setSlugTouched(true)
              setSlug(event.target.value)
            }}
            error={
              errors.slug ??
              (isApiError(save.error, 'duplicate') ? 'That slug is taken.' : undefined)
            }
          />
          {save.error && !isApiError(save.error, 'duplicate') ? (
            <p role="alert" className="text-sm text-danger">
              {errorMessage(save.error)}
            </p>
          ) : null}
        </form>
      </Dialog>
      {dialog}
    </>
  )
}

/** Admin → Orgs (P-ADM-005): create, edit, suspend and resume organisations. */
export function OrgsPage() {
  useTitle('Organisations')
  const { toast } = useToast()
  const { guard, dialog } = useStepUp()
  const [status, setStatus] = useState('')
  const pager = useCursorPager()
  const orgs = useAdminOrgs({ status, limit: 25, cursor: pager.cursor })
  const [editing, setEditing] = useState<AdminOrg | null>(null)
  const [creating, setCreating] = useState(false)
  const [suspending, setSuspending] = useState<AdminOrg | null>(null)
  const [resuming, setResuming] = useState<AdminOrg | null>(null)

  const suspend = useApiMutation(
    (vars: { id: string; revision: number; reason: string }, ctx) =>
      guard(() => adminApi.suspendOrg(vars, ctx)),
    {
      invalidate: [adminKeys.orgs, adminKeys.users],
      onSuccess: () => {
        setSuspending(null)
        toast({ tone: 'success', message: 'Organisation suspended. Its members were signed out.' })
      },
    },
  )
  const resume = useApiMutation(
    (vars: { id: string; revision: number }, ctx) => guard(() => adminApi.resumeOrg(vars, ctx)),
    {
      invalidate: [adminKeys.orgs, adminKeys.users],
      onSuccess: () => {
        setResuming(null)
        toast({ tone: 'success', message: 'Organisation resumed.' })
      },
    },
  )

  const columns: Column<AdminOrg>[] = [
    {
      id: 'name',
      header: 'Organisation',
      cell: (o) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{o.name}</p>
          <p className="truncate font-mono text-xs text-sage">{o.slug}</p>
        </div>
      ),
    },
    {
      id: 'kind',
      header: 'Type',
      cell: (o) => (
        <Badge tone={o.kind === 'fruuit' ? 'brand' : 'outline'}>
          {o.kind === 'fruuit' ? 'Fruuit' : 'Investor'}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (o) => (
        <div>
          <StatusBadge status={o.status} />
          {o.suspend_reason ? (
            <p className="mt-1 max-w-56 truncate text-xs text-sage">{o.suspend_reason}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'members',
      header: 'People',
      align: 'right',
      cell: (o) => (
        <Link to={`/admin/users?org=${o.id}`} className="text-teal hover:underline">
          {o.member_count}
          {o.pending_invites > 0 ? (
            <span className="text-sage"> (+{o.pending_invites} invited)</span>
          ) : null}
        </Link>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      cell: (o) => <span className="text-sage">{formatDate(o.created_at)}</span>,
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (o) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setEditing(o)}>
            Edit
          </Button>
          {o.kind === 'investor' ? (
            o.status === 'active' ? (
              <Button
                size="sm"
                variant="secondary"
                icon={<Pause />}
                onClick={() => setSuspending(o)}
              >
                Suspend
              </Button>
            ) : (
              <Button size="sm" variant="secondary" icon={<Play />} onClick={() => setResuming(o)}>
                Resume
              </Button>
            )
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Organisations"
        title="Investor organisations and Fruuit."
        description="Create and edit organisations. Suspending one signs its people out at once and blocks sign-in until it is resumed."
        actions={
          <Button icon={<Plus />} onClick={() => setCreating(true)}>
            New organisation
          </Button>
        }
      />
      <div className="mb-5 max-w-56">
        <Select
          label="Status"
          name="status"
          value={status}
          placeholder="Any status"
          options={[
            { value: 'active', label: 'Active' },
            { value: 'suspended', label: 'Suspended' },
          ]}
          onChange={(event) => {
            setStatus(event.target.value)
            pager.reset()
          }}
        />
      </div>
      {orgs.isError ? (
        isApiError(orgs.error, 'mfa_required') ? (
          <MfaRequiredNotice onVerified={() => void orgs.refetch()} />
        ) : (
          <Callout tone="danger" role="alert">
            {errorMessage(orgs.error)}
          </Callout>
        )
      ) : (
        <DataTable
          caption="Organisations"
          columns={columns}
          rows={orgs.data?.items ?? []}
          getRowId={(o) => o.id}
          loading={orgs.isPending}
          empty={
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <Building aria-hidden="true" className="size-6 text-sage" />
              <p className="font-medium text-ink">No organisations yet</p>
            </div>
          }
          pagination={{
            mode: 'server',
            hasPrevious: pager.hasPrevious,
            hasNext: Boolean(orgs.data?.next_cursor),
            onPrevious: pager.previous,
            onNext: () => pager.next(orgs.data?.next_cursor ?? null),
            summary: `Page ${pager.page}`,
          }}
        />
      )}
      <p className="mt-4 flex items-center gap-2 text-xs text-sage">
        <Users aria-hidden="true" className="size-4" />
        People counts are active members; invited people are shown in brackets.
      </p>

      <OrgDialog
        open={creating || editing !== null}
        org={editing}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
      />
      <ReasonDialog
        open={suspending !== null}
        title={`Suspend ${suspending?.name ?? ''}?`}
        description="Everyone in this organisation is signed out now and can't sign in until you resume it."
        confirmLabel="Suspend"
        variant="danger"
        withReason
        pending={suspend.isPending}
        error={suspend.error ? errorMessage(suspend.error) : undefined}
        onConfirm={(reason) =>
          suspending && suspend.mutate({ id: suspending.id, revision: suspending.revision, reason })
        }
        onClose={() => setSuspending(null)}
      />
      <ReasonDialog
        open={resuming !== null}
        title={`Resume ${resuming?.name ?? ''}?`}
        description="Its people can sign in again."
        confirmLabel="Resume"
        pending={resume.isPending}
        error={resume.error ? errorMessage(resume.error) : undefined}
        onConfirm={() =>
          resuming && resume.mutate({ id: resuming.id, revision: resuming.revision })
        }
        onClose={() => setResuming(null)}
      />
      {dialog}
    </>
  )
}
